import React from 'react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, writeBatch, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User as AppUser } from '../types';
import { trackUserLogin } from '../utils/analytics';
import './SSOButtons.css';

interface SSOButtonsProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  disabled?: boolean;
}

const SSOButtons: React.FC<SSOButtonsProps> = ({ onSuccess, onError, disabled = false }) => {
  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      if (user) {
        // Check if user profile exists by UID first
        let userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (!userDoc.exists()) {
          // Check if a user with this email already exists (from regular registration)
          const usersQuery = query(
            collection(db, 'users'),
            where('email', '==', user.email)
          );
          const existingUsers = await getDocs(usersQuery);
          
          if (!existingUsers.empty) {
            // Found existing account with same email - merge the accounts
            const existingUserDoc = existingUsers.docs[0];
            const existingUserData = existingUserDoc.data();
            
            // Update the existing user document to use the new Google UID
            // and merge Google profile data
            const mergedUser: AppUser = {
              ...existingUserData,
              uid: user.uid, // Update to new Google UID
              name: user.displayName || existingUserData.name || '',
              email: user.email || existingUserData.email || '',
              profileImageUrl: user.photoURL || existingUserData.profileImageUrl,
              location: existingUserData.location || '',
              joined: existingUserData.joined || new Date().toISOString(),
              // Keep existing data like company info, etc.
            } as AppUser;
            
            // Create the new user document with the Google UID
            await setDoc(doc(db, 'users', user.uid), mergedUser);
            
            // Update all user's apiaries, hives, and inspections to use new UID
            // This ensures data continuity
            const [apiaries, hives, inspections] = await Promise.all([
              getDocs(query(collection(db, 'apiaries'), where('user_id', '==', existingUserDoc.id))),
              getDocs(query(collection(db, 'hives'), where('user_id', '==', existingUserDoc.id))),
              getDocs(query(collection(db, 'inspections'), where('user_id', '==', existingUserDoc.id)))
            ]);
            
            // Update apiaries
            const batch = writeBatch(db);
            apiaries.docs.forEach(doc => {
              batch.update(doc.ref, { user_id: user.uid });
            });
            
            // Update hives  
            hives.docs.forEach(doc => {
              batch.update(doc.ref, { user_id: user.uid });
            });
            
            // Update inspections
            inspections.docs.forEach(doc => {
              batch.update(doc.ref, { user_id: user.uid });
            });
            
            await batch.commit();
            
            // Delete the old user document
            await deleteDoc(doc(db, 'users', existingUserDoc.id));
            
            // Wait a moment for Firebase auth state to fully propagate
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            // No existing account - create new user profile for first-time Google sign-in
            const newUser: AppUser = {
              uid: user.uid,
              name: user.displayName || '',
              email: user.email || '',
              location: '', // Will need to be filled later
              joined: new Date().toISOString(),
              profileImageUrl: user.photoURL || undefined
            };
            
            await setDoc(doc(db, 'users', user.uid), newUser);
          }
        }
        
        // Track successful Google login
        trackUserLogin('google');
        
        onSuccess?.();
      }
    } catch (error: any) {
      console.error('Google Sign-In Error:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to sign in with Google';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in cancelled';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup blocked. Please allow popups and try again';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection';
      }
      
      onError?.(errorMessage);
    }
  };

  return (
    <div className="sso-buttons">
      <button
        type="button"
        className="google-sso-button"
        onClick={handleGoogleSignIn}
        disabled={disabled}
      >
        <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </button>

      <div className="sso-divider">
        <span>or</span>
      </div>
    </div>
  );
};

export default SSOButtons;
